// src/pages/motolivraison/commerce-detail.page.jsx
//
// Détail d'un commerce avec toutes ses informations + actions admin.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Statistic, Button, message, Space,
  Breadcrumb, Avatar, Spin, Tabs, Table, Empty, Typography,
} from 'antd';
import {
  ShopOutlined, ArrowLeftOutlined, RightOutlined, CheckCircleOutlined,
  StopOutlined, PlayCircleOutlined, AppstoreOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';
import dayjs from 'dayjs';

const { Text } = Typography;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Libellés + couleurs des statuts de commande
const STATUT_CMD = {
  en_attente_paiement: { c: 'orange', l: 'Attente paiement' },
  payee: { c: 'blue', l: 'Payée' },
  en_attente_livreur: { c: 'gold', l: 'Attente livreur' },
  assignee: { c: 'cyan', l: 'Assignée' },
  recuperee: { c: 'cyan', l: 'Récupérée' },
  en_livraison: { c: 'geekblue', l: 'En livraison' },
  livree: { c: 'green', l: 'Livrée' },
  confirmee: { c: 'green', l: 'Confirmée' },
  soldee: { c: 'success', l: 'Soldée' },
  annulee: { c: 'red', l: 'Annulée' },
};

const CommerceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commerce, setCommerce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [commandes, setCommandes] = useState([]);
  const [loadingCommandes, setLoadingCommandes] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCommerceById(id);
      setCommerce(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Articles + commandes du commerce (onglets)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingArticles(true);
      try {
        const r = await motolivraisonService.getCommerceArticles(id);
        if (alive) setArticles(r?.articles || []);
      } catch (e) { /* silencieux */ } finally { if (alive) setLoadingArticles(false); }
    })();
    (async () => {
      setLoadingCommandes(true);
      try {
        const r = await motolivraisonService.listCommandes({ commerceId: id, limit: 100 });
        if (alive) setCommandes(r?.commandes || []);
      } catch (e) { /* silencieux */ } finally { if (alive) setLoadingCommandes(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const handleAction = async (action) => {
    try {
      const labels = { validate: 'approuvé', suspend: 'suspendu', reactivate: 'réactivé' };
      if (action === 'validate') await motolivraisonService.validateCommerce(id);
      else if (action === 'suspend') await motolivraisonService.suspendCommerce(id);
      else if (action === 'reactivate') await motolivraisonService.reactivateCommerce(id);
      message.success(`Commerce ${labels[action]}`);
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !commerce) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red', rejected: 'default' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu', rejected: 'Rejeté' })[s] || s;

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/commerces">Commerces</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{commerce.nom}</Breadcrumb.Item>
        </Breadcrumb>

        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
          Retour
        </Button>

        <Card>
          <Row gutter={24}>
            <Col xs={24} md={6} style={{ textAlign: 'center' }}>
              <Avatar size={120} src={commerce.logo} icon={<ShopOutlined />} style={{ backgroundColor: '#fa8c16' }} />
              <h2 style={{ marginTop: 16 }}>{commerce.nom}</h2>
              <Tag color={statusColor(commerce.status)} style={{ fontSize: 13, padding: '4px 12px' }}>
                {statusLabel(commerce.status)}
              </Tag>
            </Col>

            <Col xs={24} md={18}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Catégorie" span={2}>
                  {commerce.categorieId?.nom || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Adresse" span={2}>{commerce.adresse}</Descriptions.Item>
                {commerce.quartier && (
                  <Descriptions.Item label="Quartier">{commerce.quartier}</Descriptions.Item>
                )}
                <Descriptions.Item label="Ville">{commerce.cityId?.name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Téléphone">{commerce.telephone}</Descriptions.Item>
                {commerce.email && (
                  <Descriptions.Item label="Email">{commerce.email}</Descriptions.Item>
                )}
                {commerce.description && (
                  <Descriptions.Item label="Description" span={2}>{commerce.description}</Descriptions.Item>
                )}
              </Descriptions>

              {commerce.ownerId && (
                <Card size="small" title="Propriétaire" style={{ marginTop: 16 }}>
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Nom">
                      {commerce.ownerId.firstName} {commerce.ownerId.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Téléphone">{commerce.ownerId.phone}</Descriptions.Item>
                    {commerce.ownerId.email && (
                      <Descriptions.Item label="Email" span={2}>{commerce.ownerId.email}</Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              )}
            </Col>
          </Row>
        </Card>

        <Card title="Statistiques" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col xs={6}><Statistic title="Articles" value={commerce.stats?.totalArticles || 0} /></Col>
            <Col xs={6}><Statistic title="Articles actifs" value={commerce.stats?.articlesActifs || 0} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col xs={6}><Statistic title="Commandes" value={commerce.stats?.totalCommandes || 0} /></Col>
            <Col xs={6}><Statistic title="Revenus brut" value={commerce.stats?.revenusBrut || 0} suffix=" F" /></Col>
          </Row>
        </Card>

        <Card style={{ marginTop: 16 }}>
          <Tabs
            defaultActiveKey="articles"
            items={[
              {
                key: 'articles',
                label: <span><AppstoreOutlined /> Articles ({articles.length})</span>,
                children: (
                  <Table
                    rowKey="_id"
                    size="small"
                    loading={loadingArticles}
                    dataSource={articles}
                    locale={{ emptyText: <Empty description="Aucun article enregistré" /> }}
                    pagination={{ pageSize: 10, showTotal: (t) => `${t} article(s)` }}
                    columns={[
                      {
                        title: 'Article', key: 'art',
                        render: (_, r) => (
                          <Space>
                            <Avatar shape="square" size={42} src={r.photo} icon={<AppstoreOutlined />} style={{ backgroundColor: '#fff7e6', color: '#fa8c16' }} />
                            <div>
                              <Text strong>{r.nom}</Text>
                              {r.sousCategorie && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.sousCategorie}</Text></div>}
                            </div>
                          </Space>
                        ),
                      },
                      { title: 'Prix', dataIndex: 'prix', key: 'prix', align: 'right', render: (v, r) => <Text strong>{fmt(v)} F{r.unite ? <Text type="secondary" style={{ fontSize: 11 }}>/{r.unite}</Text> : ''}</Text> },
                      { title: 'Stock', dataIndex: 'stock', key: 'stock', align: 'center', render: (v) => (v == null ? '—' : fmt(v)) },
                      { title: 'Vendus', key: 'vendus', align: 'center', render: (_, r) => fmt(r.stats?.quantiteVendue) },
                      { title: 'Statut', key: 'st', align: 'center', render: (_, r) => <Tag color={r.isActive ? 'green' : 'default'}>{r.isActive ? 'Actif' : 'Inactif'}</Tag> },
                    ]}
                  />
                ),
              },
              {
                key: 'commandes',
                label: <span><ShoppingCartOutlined /> Commandes ({commandes.length})</span>,
                children: (
                  <Table
                    rowKey="_id"
                    size="small"
                    loading={loadingCommandes}
                    dataSource={commandes}
                    locale={{ emptyText: <Empty description="Aucune commande" /> }}
                    pagination={{ pageSize: 10, showTotal: (t) => `${t} commande(s)` }}
                    columns={[
                      { title: 'Réf', key: 'ref', render: (_, r) => <Text code>#{String(r._id).slice(-6).toUpperCase()}</Text> },
                      {
                        title: 'Client', key: 'cli',
                        render: (_, r) => {
                          const nom = r.clientNom || (r.clientId ? `${r.clientId.firstName || ''} ${r.clientId.lastName || ''}`.trim() : '');
                          return <div><Text>{nom || '—'}</Text><div><Text type="secondary" style={{ fontSize: 12 }}>{r.clientPhone || r.clientId?.phone || ''}</Text></div></div>;
                        },
                      },
                      { title: 'Mode', dataIndex: 'mode', key: 'mode', render: (v) => <Tag>{v === 'telephone' ? 'Téléphone' : 'App'}</Tag> },
                      { title: 'Montant', dataIndex: 'montantTTC', key: 'mt', align: 'right', render: (v) => <Text strong>{fmt(v)} F</Text> },
                      { title: 'Statut', dataIndex: 'statut', key: 'st', render: (v) => <Tag color={STATUT_CMD[v]?.c || 'default'}>{STATUT_CMD[v]?.l || v}</Tag> },
                      { title: 'Date', dataIndex: 'createdAt', key: 'dt', render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—' },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Card>

        <Card title="Actions" style={{ marginTop: 16 }}>
          <Space wrap>
            {commerce.status === 'pending' && (
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleAction('validate')}>
                Approuver
              </Button>
            )}
            {commerce.status === 'approved' && (
              <Button danger icon={<StopOutlined />} onClick={() => handleAction('suspend')}>
                Suspendre
              </Button>
            )}
            {commerce.status === 'suspended' && (
              <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => handleAction('reactivate')}>
                Réactiver
              </Button>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default CommerceDetail;
